/**
 * PHASE 2: Subscription & License Management
 * 
 * This component handles recurring payments for:
 * - Model licenses (monthly/annual)
 * - Feature subscriptions
 * - Software access tiers
 * 
 * Integration: Import this alongside Phase 1 hardware checkout
 */

import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const SubscriptionCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Subscription tiers for model licenses and features
  const subscriptionPlans = [
    {
      id: 'basic-monthly',
      name: 'Basic Model Access',
      description: 'Access to core AI models and basic features',
      priceMonthly: 1900, // $19.00/month
      priceAnnual: 19900, // $199.00/year (save ~$30)
      features: [
        'Access to 5 core AI models',
        'Basic task automation',
        'Standard support',
        'Community access',
        'Monthly model updates'
      ],
      tier: 'basic'
    },
    {
      id: 'pro-monthly',
      name: 'Professional License',
      description: 'Advanced models with enhanced capabilities',
      priceMonthly: 4900, // $49.00/month
      priceAnnual: 49900, // $499.00/year (save ~$90)
      features: [
        'Access to ALL AI models',
        'Advanced task automation',
        'Priority support',
        'Private model training',
        'API access (10k requests/month)',
        'Weekly model updates',
        'Custom integrations'
      ],
      tier: 'professional',
      popular: true
    },
    {
      id: 'enterprise-monthly',
      name: 'Enterprise Suite',
      description: 'Unlimited access with dedicated support',
      priceMonthly: 14900, // $149.00/month
      priceAnnual: 149900, // $1,499.00/year (save ~$290)
      features: [
        'Unlimited model access',
        'Custom model development',
        'Dedicated account manager',
        'SLA guarantees',
        'Unlimited API requests',
        'Real-time model updates',
        'White-label options',
        'On-premise deployment'
      ],
      tier: 'enterprise'
    }
  ];

  // Individual model licenses (one-time or subscription)
  const modelLicenses = [
    {
      id: 'model-vision-pro',
      name: 'Vision Processing Model',
      description: 'Advanced image and video analysis',
      priceMonthly: 900, // $9.00/month
      priceAnnual: 9900, // $99.00/year
      category: 'Vision AI'
    },
    {
      id: 'model-nlp-advanced',
      name: 'NLP Advanced Suite',
      description: 'Natural language processing and generation',
      priceMonthly: 1200, // $12.00/month
      priceAnnual: 12900, // $129.00/year
      category: 'Language AI'
    },
    {
      id: 'model-audio-processor',
      name: 'Audio Processing Model',
      description: 'Speech recognition and audio analysis',
      priceMonthly: 700, // $7.00/month
      priceAnnual: 7900, // $79.00/year
      category: 'Audio AI'
    },
    {
      id: 'model-predictive-analytics',
      name: 'Predictive Analytics Engine',
      description: 'Data forecasting and trend analysis',
      priceMonthly: 1500, // $15.00/month
      priceAnnual: 15900, // $159.00/year
      category: 'Analytics'
    }
  ];

  /**
   * Creates a Stripe Checkout Session for subscription
   */
  const handleSubscriptionCheckout = async (plan, billingCycle) => {
    setLoading(true);
    setError(null);

    const price = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;

    try {
      const response = await fetch('/api/create-subscription-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: plan.id,
          planName: plan.name,
          planPrice: price,
          billingCycle: billingCycle,
          tier: plan.tier || 'individual',
          features: plan.features || []
        }),
      });

      const { sessionId, error: apiError } = await response.json();

      if (apiError) {
        throw new Error(apiError);
      }

      const stripe = await stripePromise;
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) {
        throw stripeError;
      }
    } catch (err) {
      setError(err.message);
      console.error('Subscription checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  return (
    <div className="subscription-checkout-container">
      {/* Subscription Tiers */}
      <div className="subscription-section">
        <div className="section-header">
          <h1>Choose Your Plan</h1>
          <p className="section-subtitle">
            Select the perfect subscription tier for your needs
          </p>
        </div>

        <div className="pricing-toggle">
          <button 
            className={`toggle-btn ${selectedPlan?.billing === 'monthly' ? 'active' : ''}`}
            onClick={() => setSelectedPlan({ ...selectedPlan, billing: 'monthly' })}
          >
            Monthly
          </button>
          <button 
            className={`toggle-btn ${selectedPlan?.billing === 'annual' ? 'active' : ''}`}
            onClick={() => setSelectedPlan({ ...selectedPlan, billing: 'annual' })}
          >
            Annual
            <span className="save-badge">Save up to 20%</span>
          </button>
        </div>

        <div className="pricing-cards">
          {subscriptionPlans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              billingCycle={selectedPlan?.billing || 'monthly'}
              onCheckout={handleSubscriptionCheckout}
              loading={loading}
              formatPrice={formatPrice}
            />
          ))}
        </div>
      </div>

      {/* Individual Model Licenses */}
      <div className="subscription-section model-licenses">
        <div className="section-header">
          <h2>Individual Model Licenses</h2>
          <p className="section-subtitle">
            Add specific AI models to your toolkit
          </p>
        </div>

        <div className="model-grid">
          {modelLicenses.map((model) => (
            <ModelLicenseCard
              key={model.id}
              model={model}
              billingCycle={selectedPlan?.billing || 'monthly'}
              onCheckout={handleSubscriptionCheckout}
              loading={loading}
              formatPrice={formatPrice}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
};

/**
 * Pricing Card Component for subscription tiers
 */
const PricingCard = ({ plan, billingCycle, onCheckout, loading, formatPrice }) => {
  const price = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
  const pricePerMonth = billingCycle === 'annual' ? price / 12 : price;

  return (
    <div className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
      {plan.popular && <div className="popular-badge">Most Popular</div>}
      
      <div className="pricing-header">
        <h3 className="plan-name">{plan.name}</h3>
        <p className="plan-description">{plan.description}</p>
        
        <div className="plan-price">
          <span className="price-amount">{formatPrice(pricePerMonth)}</span>
          <span className="price-period">/month</span>
        </div>
        
        {billingCycle === 'annual' && (
          <p className="billing-note">
            Billed annually at {formatPrice(price)}
          </p>
        )}
      </div>

      <div className="plan-features">
        <ul>
          {plan.features.map((feature, index) => (
            <li key={index}>
              <span className="feature-icon">✓</span>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      <button
        className={`subscribe-button ${plan.popular ? 'primary' : 'secondary'}`}
        onClick={() => onCheckout(plan, billingCycle)}
        disabled={loading}
      >
        {loading ? 'Processing...' : 'Get Started'}
      </button>
    </div>
  );
};

/**
 * Model License Card Component
 */
const ModelLicenseCard = ({ model, billingCycle, onCheckout, loading, formatPrice }) => {
  const price = billingCycle === 'annual' ? model.priceAnnual : model.priceMonthly;

  return (
    <div className="model-license-card">
      <div className="model-category">{model.category}</div>
      <h4 className="model-name">{model.name}</h4>
      <p className="model-description">{model.description}</p>
      
      <div className="model-pricing">
        <span className="model-price">{formatPrice(price)}</span>
        <span className="model-period">
          /{billingCycle === 'annual' ? 'year' : 'month'}
        </span>
      </div>

      <button
        className="model-subscribe-button"
        onClick={() => onCheckout(model, billingCycle)}
        disabled={loading}
      >
        {loading ? 'Processing...' : 'Subscribe'}
      </button>
    </div>
  );
};

export default SubscriptionCheckout;
